// Board rendering: a pure projection of the store's view entry (exact state
// at the replay cursor) plus the legal-mask read model. No rules logic lives
// here — legality is displayed from the server's mask, never computed.

import type { CSSProperties, MouseEvent } from "react";

import {
  actionIdOf,
  boardOffset,
  canvasPointOf,
  fromCanvas,
  kindOf,
  PASS_ACTION_ID,
  type ActionKind,
} from "../core/codec.js";
import type { ExactState, LegalMaskPayload, Player, AnalysisUpdatePayload } from "../core/wire.js";
import { stoneDecoration } from "./stone-decoration.js";
import { SkipForward } from "lucide-react";

export interface BoardProps {
  showCoordinates?: boolean;
  analysis?: AnalysisUpdatePayload | null;
  state: ExactState;
  legalMask: LegalMaskPayload | null;
  selectedKind: ActionKind;
  // True only when a click may produce a command (live, baseline present,
  // not replaying, non-terminal). The authoritative verdict is the server's.
  interactive: boolean;
  onPoint: (boardX: number, boardY: number) => void;
  onPass: () => void;
}

function legalSetFor(mask: LegalMaskPayload | null, kind: ActionKind | "PASS"): Set<number> | null {
  if (mask === null) return null;
  const set = new Set<number>();
  for (const range of mask.legalActionRanges)
    for (let id = range.first; id <= range.last; id++) set.add(id);
  if (kind === "PASS") return set.has(PASS_ACTION_ID) ? set : new Set();
  const base = { NORMAL: 0, IMMORTAL: 361, DOUBLE_START: 722, EIGHTWAY: 1083 }[kind];
  const filtered = new Set<number>();
  for (const id of set) if (id >= base && id < base + 361) filtered.add(id);
  return filtered;
}

function cellLabel(state: ExactState, index: number): string {
  const cell = state.cells[index]!;
  if (cell.color === null) return "";
  return cell.color === "BLACK" ? "●" : "○";
}

function cellMark(state: ExactState, index: number): string {
  const source = state.cells[index]!.source;
  if (source === null) return "";
  const kind = source.originKind;
  if (kind === "IMMORTAL") return "I";
  if (kind === "DOUBLE_START") return "D";
  if (kind === "EIGHTWAY") return "E";
  return "";
}

export function boardLinkMap(state: ExactState): Set<number> {
  // Cells whose specialLink is shared by at least one other cell (a
  // DOUBLE_START pair links both stones to the same origin action number).
  const linkCounts = new Map<number, number>();
  for (const cell of state.cells) {
    const link = cell.source?.specialLink;
    if (link !== null && link !== undefined) linkCounts.set(link, (linkCounts.get(link) ?? 0) + 1);
  }
  const linked = new Set<number>();
  for (let i = 0; i < state.cells.length; i++) {
    const link = state.cells[i]!.source?.specialLink;
    if (link !== null && link !== undefined && (linkCounts.get(link) ?? 0) > 1) linked.add(i);
  }
  return linked;
}

export function BoardLines({ size }: { size: number }) {
  // Lines and button/stone centers share one pitch: half a pitch of margin,
  // then N intersections spanning N-1 intervals (also used by Lizzie).
  const end = size - 0.5;
  const starEdge = size === 9 ? 2 : 3;
  const middle = (size - 1) / 2;
  const stars = size === 19
    ? [starEdge, middle, size - 1 - starEdge].flatMap((x) =>
      [starEdge, middle, size - 1 - starEdge].map((y) => [x, y]))
    : size === 9 || size === 13
      ? [[starEdge, starEdge], [starEdge, size - 1 - starEdge],
        [size - 1 - starEdge, starEdge], [size - 1 - starEdge, size - 1 - starEdge], [middle, middle]]
      : [];
  return <svg className="board-lines" viewBox={`0 0 ${size} ${size}`} aria-hidden="true" focusable="false">
    {Array.from({ length: size }, (_, i) => <g key={i}>
      <line x1={0.5} y1={i + 0.5} x2={end} y2={i + 0.5} />
      <line x1={i + 0.5} y1={0.5} x2={i + 0.5} y2={end} />
    </g>)}
    {stars.map(([x, y]) => <circle key={`${x},${y}`} cx={x! + 0.5} cy={y! + 0.5} r={0.095} />)}
  </svg>;
}

export function Board(props: BoardProps) {
  const { state, interactive } = props;
  const size = state.boardSize;
  const legal = legalSetFor(props.legalMask, props.selectedKind);
  const passLegal = legalSetFor(props.legalMask, "PASS")?.has(PASS_ACTION_ID) ?? null;
  const links = boardLinkMap(state);
  const offset = boardOffset(size);
  const decoration = stoneDecoration(state);
  const currentAnalysis = props.analysis?.basisRevision === state.revision && props.analysis?.basisSemanticEventCursor === state.semanticEventCursor && state.phase !== "TERMINAL" ? props.analysis : null;
  const suggestions = new Map((currentAnalysis?.moves ?? []).filter((row) =>
    row.visits > 0 && row.action.kind === props.selectedKind).sort((a, b) => b.visits - a.visits)
    .slice(0, 8).map((row) => [row.action.actionId, row]));

  const cells = [];
  for (let y = 0; y < size; y++) {
    for (let x = 0; x < size; x++) {
      const index = y * size + x;
      const cell = state.cells[index]!;
      const canvasX = x + offset;
      const canvasY = y + offset;
      const id = actionIdOf(props.selectedKind, canvasX, canvasY);
      const isLegal = legal !== null && legal.has(id);
      const linked = links.has(index);      const classNames = ["cell"];
      if (cell.color !== null) classNames.push(cell.color === "BLACK" ? "black" : "white");
      if (isLegal && interactive) classNames.push("legal");
      if (linked) classNames.push("linked");
      if (decoration.glow.has(index)) classNames.push("immortal-glow");
      const mark = cellMark(state, index);
      const suggestion = suggestions.get(id);
      cells.push(
        <button
          key={index}
          className={classNames.join(" ")}
          disabled={!interactive}
          data-x={x}
          data-y={y}
          aria-label={`${actionLabel(id)}${cell.color === null ? "" : ` ${cell.color}`}`}
          data-legal={isLegal ? "true" : undefined}
          onClick={(event: MouseEvent) => {
            event.preventDefault();
            if (interactive) props.onPoint(x, y);
          }}
        >
          <span className="stone" aria-hidden="true">{cellLabel(state, index)}</span>
          {mark !== "" && <span className="mark">{mark}</span>}
          {suggestion !== undefined && <span className="analysis-point"
            title={`胜率估计 ${((suggestion.qActorMicro / 1000000 + 1) * 50).toFixed(1)}% · 访问 ${suggestion.visits} · Q ${(suggestion.qActorMicro / 1000000).toFixed(3)}；按当前行动方的 (Q+1)/2 映射，无结果计半；后端未提供目差`}>
            <strong>{((suggestion.qActorMicro / 1000000 + 1) * 50).toFixed(0)}%</strong>
            <small>{suggestion.visits >= 1000 ? `${(suggestion.visits / 1000).toFixed(1)}k` : suggestion.visits}</small>
          </span>}
        </button>,
      );
    }
  }

  const style: CSSProperties = {
    gridTemplateColumns: `repeat(${size}, 1fr)`,
  };
  return (
    <div className="board-wrap">
      <div className="board-stage">
      <div className={`board size-${size}`} style={style} data-board-size={size}>
        <BoardLines size={size} />
        <svg className="special-connections" viewBox={`0 0 ${size} ${size}`} aria-hidden="true">
          {decoration.diagonals.map(({ from, to, color, connected }) => {
            const x = from % size + .5, y = Math.floor(from / size) + .5;
            const tx = to % size + .5, ty = Math.floor(to / size) + .5;
            const length = connected ? 1 : .66;
            return <line key={`${from}-${to}`} className={`eightway-edge ${color.toLowerCase()}`}
              data-connected={connected} x1={x} y1={y} x2={x + (tx - x) * length} y2={y + (ty - y) * length} />;
          })}
        </svg>
        {props.showCoordinates !== false && <div className="board-coordinates" aria-hidden="true">
          {Array.from({ length: size }, (_, i) => <span key={i}>
            <span className="coordinate top" style={{ left: `${(i + .5) / size * 100}%` }}>{String.fromCharCode(65 + i)}</span>
            <span className="coordinate bottom" style={{ left: `${(i + .5) / size * 100}%` }}>{String.fromCharCode(65 + i)}</span>
            <span className="coordinate left" style={{ top: `${(i + .5) / size * 100}%` }}>{i + 1}</span>
            <span className="coordinate right" style={{ top: `${(i + .5) / size * 100}%` }}>{i + 1}</span>
          </span>)}
        </div>}
        {cells}
      </div>
      </div>
      <div className="board-footer">
        <button
          className="pass"
          disabled={!interactive || passLegal === false || state.pendingDouble !== null}
          data-pass-legal={passLegal === null ? undefined : String(passLegal)}
          onClick={() => props.onPass()}
        >
          <SkipForward size={16} /> Pass{passLegal === true ? " ✓" : passLegal === false ? " ✗" : ""}
        </button>
        <span className="mask-basis">
          {props.legalMask === null
            ? "legal mask: not loaded"
            : `legal mask @rev ${props.legalMask.revision} (${props.legalMask.legalActionCount} actions)`}
        </span>
      </div>
    </div>
  );
}

// Coordinate label for display (canvas row/col letters follow the frozen
// codec; 19×19 canvas, skip nothing — display-only convention).
export function actionLabel(actionId: number): string {
  if (actionId === PASS_ACTION_ID) return "PASS";
  const point = canvasPointOf(actionId);
  if (point === null) return "?";
  const kind = kindOf(actionId);
  const letter = String.fromCharCode(65 + point.x);
  return `${kind === "PASS" ? "" : `${kind} `}${letter}${point.y + 1}`;
}

export function playerLabel(player: Player): string {
  if (player === "BLACK") return "黑";
  if (player === "WHITE") return "白";
  return "—";
}

export { fromCanvas };
